import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getPatientNotes } from "../../../services/patient/details";
import { getCarePlan } from "../../../services/patient/carePlan";
import { getPatientMedications } from "../../../services/admin/medicationOptions";
import { getPatientGeneralDetails } from "../../../services/patient/details";
import { getAuditReport, getSummary } from "../../../services/case-manager/billings";
import { formatDate, formatSecondsToTime } from "../../../utils/helper";
import { formatUSADate, formatUSADateTime, formatNaiveDateTime } from "../../../utils/dateFormatters";
import Button from "../../../components/button-v1";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

// New imports for PDF generation
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import logo from "../../../assets/images/toca_health_pdf_logo.png";
import CarePlanAuditReportPDF from "./CarePlanAuditReportPDF";
import Spinner from "../../spinner";
import { buildReportFilename, DOCTYPES } from "../../../utils/fileNaming";
import { SYMPTOM_STATES, parseSymptomsString, wasCreatedOnOrAfterSymptomsCutoff } from "../../../utils/symptomTriState";
import { getHeightUnit } from "../../../utils/heightUnit";
import { parseBoldSegments } from "../../../utils/parseBoldMarkers";

import styles from "./styles.module.css";

const CareplanViewPdf = () => {
  const [params] = useSearchParams();
  const [patient_id, setPatientId] = useState(sessionStorage.getItem("_pt_id"));
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();
  const monthParam = params.get("month");
  const yearParam = params.get("year");

  const parsedMonth = monthParam ? parseInt(monthParam, 10) : null;
  const parsedYear = yearParam ? parseInt(yearParam, 10) : null;

  const [selectedMonth, setSelectedMonth] = useState({
    month:
      parsedMonth && parsedMonth >= 1 && parsedMonth <= 12
        ? parsedMonth
        : currentMonth,
    year: parsedYear && parsedYear > 0 ? parsedYear : currentYear,
  });

  const locationId = JSON.parse(sessionStorage.getItem("current_loc"))?.id;
  const noteType = "ccm_note";
  const currentSort = "created_at";
  const [downloading, setDownloading] = useState(false);
  const [billingData, setBillingData] = useState(null);

  const { data: patient, isLoading: patientLoading } = useQuery(
    ["patient-general-details", patient_id],
    () => getPatientGeneralDetails(patient_id),
    { enabled: !!patient_id }
  );

  const { data: carePlanData, isFetching: carePlan_loading } = useQuery({
    queryKey: ["carePlan", patient_id],
    queryFn: () => getCarePlan(selectedMonth.month, selectedMonth.year),
    enabled: !!patient_id,
  });

  // SCRUM-738: this used to fetch the separate, legacy `patient-conditions`
  // API instead of reading the chronic_conditions already nested on the
  // careplan payload above - meaning edits made in the care plan wizard
  // (careplanNew.js, which reads/writes carePlanData.data.chronic_conditions
  // directly) would never show up here. Reading the same field the wizard
  // itself uses keeps this PDF/preview in sync with what was actually typed.
  const rawPatientConditions = carePlanData?.data?.chronic_conditions;

  const { data: rawPatientMedications, isLoading: patient_medication_loading } = useQuery({
    queryKey: ["PatientMedications", carePlanData?.data?.id],
    queryFn: () => getPatientMedications(carePlanData?.data?.id, locationId),
    enabled: !!carePlanData?.data?.id,
  });

  const { data: notes, isLoading: isNotesLoading } = useQuery(
    ["patient-notes", patient_id, noteType, currentSort],
    () => getPatientNotes(noteType, currentSort, patient_id, locationId),
    { enabled: !!patient_id }
  );

  const { isLoading: isBillingLoading } = useQuery(
    ["billing-summary", selectedMonth.year, selectedMonth.month, locationId],
    () => {
      const dateStr = `${selectedMonth.year}-${String(selectedMonth.month).padStart(2, "0")}`;
      return getSummary(dateStr, "ccm", locationId, undefined, undefined, undefined, 1, false);
    },
    {
      enabled: !!locationId && !!selectedMonth.year && !!selectedMonth.month,
      onSuccess: (data) => {
        if (data?.patients) {
          const foundPatient = data.patients.find((p) => {
            const apiId = String(p.patient_id).replace(/^P/, "");
            const currentId = String(patient_id).replace(/^P/, "");
            return apiId === currentId;
          });
          setBillingData(foundPatient || null);
        }
      },
    }
  );

  // Fetch audit report data for monthly monitoring time and sessions
  const { data: auditReportData, isLoading: isAuditReportLoading } = useQuery(
    ["ccm-audit-report", patient_id, selectedMonth.year, selectedMonth.month],
    () => {
      const dateStr = `${selectedMonth.year}-${String(selectedMonth.month).padStart(2, "0")}`;
      return getAuditReport(patient_id, dateStr);
    },
    { enabled: !!patient_id && !!selectedMonth.year && !!selectedMonth.month }
  );

  useEffect(() => {
    const patientId = params.get("patientId");
    if (patientId) {
      setPatientId(patientId);
    }
  }, [params]);

  // Derived Values
  const patientName = `${patient?.user?.first_name || ""} ${patient?.user?.last_name || ""}`;
  
  // Full Month Name for Report Title (e.g. December 2025)
  const monthName = new Date(selectedMonth.year, selectedMonth.month - 1).toLocaleString('en-US', { month: 'long' });
  const reportDateDisplay = `${monthName} ${selectedMonth.year}`;

  // Last Edited formatted as per utils/helper's formatDate (e.g. Dec 31st, 2025)
  const lastEditedDisplay = formatUSADateTime(carePlanData?.data?.updated_at);

  // Finalized Date for Attestation
  const finalizedDateDisplay = carePlanData?.data?.finalized_at 
    ? formatUSADateTime(carePlanData.data.finalized_at) 
    : "";

  // Finalized By Name for Attestation
  const finalizedByName = carePlanData?.data?.finalized_by_name || "";
  
  // Filter CCM Notes only for preview as well
  const ccmNotes = notes ? notes.filter(n => n.note_type === 'ccm_note') : [];

  // Filter CCM Monitoring Sessions (only those with CCM time)
  const ccmMonitoringSessions = auditReportData?.monitoring_sessions?.filter(
    session => session.total_ccm_seconds > 0
  ) || [];

  // Calculate monthly monitoring time with fallback to sum of sessions if API doesn't return total_ccm_time
  const monthlyMonitoringSeconds = auditReportData?.total_ccm_time ||
    ccmMonitoringSessions.reduce((sum, session) => sum + (session.total_ccm_seconds || 0), 0);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    const toastId = toast.loading("Generating PDF...");

    try {
      const blob = await pdf(
        <CarePlanAuditReportPDF
          patient={patient}
          carePlanData={carePlanData?.data}
          conditions={rawPatientConditions}
          medications={rawPatientMedications}
          notes={notes} // Passing all notes, filtering happens inside PDF component
          reportDate={reportDateDisplay}
          lastEditedDate={lastEditedDisplay}
          finalizedDate={finalizedDateDisplay}
          finalizedBy={finalizedByName}
          logo={logo}
          billingData={billingData}
          monthlyMonitoringTime={formatSecondsToTime(monthlyMonitoringSeconds)}
          monitoringSessions={ccmMonitoringSessions}
        />
      ).toBlob();

      saveAs(blob, buildReportFilename({
        year: selectedMonth.year,
        month: selectedMonth.month,
        patientFirstName: patient?.user?.first_name,
        patientLastName: patient?.user?.last_name,
        doctype: DOCTYPES.AUDIT,
        program: "CCM",
        ext: "pdf",
      }));
      toast.success("PDF Downloaded!", { id: toastId });
    } catch (error) {
      console.error("PDF generation failed:", error);
      toast.error("Failed to generate PDF", { id: toastId });
    } finally {
      setDownloading(false);
    }
  };

  const isLoading =
    patientLoading ||
    carePlan_loading ||
    patient_medication_loading ||
    isNotesLoading ||
    isBillingLoading ||
    isAuditReportLoading;

  if (isLoading) {
    return <div className={styles.loadingContainer}><Spinner /></div>;
  }

  // SCRUM-941: `numbered` lists (Treatment Goals, Potential Barriers,
  // Recommendations) number their non-blank lines.
  const renderList = (text, numbered = false) => {
    if (!text) return <div className={styles.listItem}></div>;
    const lines = text.split("\n");
    if (numbered) {
      return lines.filter((item) => item.trim()).map((item, i) => (
        <div key={i} className={styles.listItem}>{i + 1}. {renderBoldText(item)}</div>
      ));
    }
    return lines.map((item, i) => (
      <div key={i} className={styles.listItem}>• {renderBoldText(item)}</div>
    ));
  };

  // AI-generated Careplan Notes/Recommendations mark emphasis with literal
  // "**double asterisks**" - rendered bold in a muted grey rather than the
  // section's normal ink color, so it reads as a distinct callout without
  // competing with the actual headings.
  const renderBoldText = (text) =>
    parseBoldSegments(text).map((segment, i) =>
      segment.bold ? (
        <strong key={i} style={{ fontWeight: 700, color: "#6b7280" }}>
          {segment.text}
        </strong>
      ) : (
        <React.Fragment key={i}>{segment.text}</React.Fragment>
      )
    );

  // Mirrors CarePlanAuditReportPDF.jsx's renderSymptoms - same two-state
  // glyph/color (SCRUM-755), just as HTML instead of react-pdf nodes.
  const SYMPTOM_GLYPH = {
    [SYMPTOM_STATES.YES]: { glyph: "✓", color: "#10b981" },
    [SYMPTOM_STATES.NO]: { glyph: "✗", color: "#ef4444" },
  };

  const renderSymptoms = (text) => {
    if (!text) return <div className={styles.listItem}></div>;
    const symptoms = parseSymptomsString(text).filter((s) => s.name.trim());
    if (symptoms.length === 0) return <div className={styles.listItem}></div>;
    return symptoms.map((symptom, i) => {
      const { glyph, color } = SYMPTOM_GLYPH[symptom.state];
      return (
        <div key={i} className={styles.listItem} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span style={{ color, fontWeight: 700, width: "14px" }}>{glyph}</span>
          <span>{symptom.name}</span>
        </div>
      );
    });
  };

  const displayICDCodes = billingData?.icd_codes?.length > 0
    ? billingData.icd_codes.join(", ")
    : rawPatientConditions?.map((c) => c.icd_code).filter(Boolean).join(", ") || "";

  // Helper to handle API variations for Behavioral Data
  const getBehavioralVal = (key1, key2) => {
    return carePlanData?.data?.behavioral_data?.[key1] || carePlanData?.data?.behavioral_data?.[key2] || "";
  };

  const formatDateOfBirth = (date) => {
    const value = formatSecondsToTime(date);
    return ["--", "None", "N/A"].includes(value) ? "" : value;
  };

  return (
    <>
      <section
        style={{
          display: "flex",
          justifyContent: "flex-end",
          width: "50rem",
          padding: "20px 0 20px 0",
        }}
      >
        <Button
          loading={downloading}
          disabled={downloading}
          id="download-btn"
          onClick={handleDownloadPDF}
        >
          Download PDF
        </Button>
      </section>

      {/* Screen Preview */}
      <div className={styles.container} id="care-plan-preview">
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.logoContainer}>
            <img src={logo} alt="TOCA Health" className={styles.logo} />
            <div className={styles.headerSeparator}></div>
          </div>
          <span className={styles.locationName}>{patient?.location?.name || "Dialysis Center"}</span>
          <div className={styles.headerTitleRow}>
            <h1 className={styles.reportTitle}>CCM Audit Report</h1>
            <span className={styles.reportDate}>{reportDateDisplay}</span>
          </div>
          <div className={styles.lastEdited}>
            Last Edited: {lastEditedDisplay}
          </div>
        </div>

        {/* General Information */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.blueBar}></div>
            <h2 className={styles.sectionTitle}>General Information</h2>
          </div>
          <div className={styles.gridContainer}>
            <div className={styles.gridColumn}>
              <div className={styles.infoItem}>
                <span className={styles.label}>Patient Name:</span>
                <span className={styles.value}>{patientName}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.label}>Birthday:</span>
                <span className={styles.value}>{formatUSADate(patient?.user?.dob)}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.label}>Email:</span>
                <span className={styles.value}>{patient?.user?.email || ""}</span>
              </div>
            </div>
            <div className={styles.gridColumn}>
              <div className={styles.infoItem}>
                <span className={styles.label}>Phone:</span>
                <span className={styles.value}>{patient?.user?.primary_phone || ""}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.label}>Complex:</span>
                <span className={styles.value}>{carePlanData?.data?.complex ? "Yes" : "No"}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.label}>Monitoring Time:</span>
                <span className={styles.value}>{formatDateOfBirth(monthlyMonitoringSeconds)}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.label}>ICD-10:</span>
                <span className={styles.value}>{displayICDCodes}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Care Manager Metadata */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.blueBar}></div>
            <h2 className={styles.sectionTitle}>Care Manager Metadata</h2>
          </div>
          <div className={styles.gridContainer}>
            <div className={styles.gridColumn}>
              <div className={styles.infoItem}>
                <span className={styles.label}>Care Manager:</span>
                <span className={styles.value}>
                  {patient?.case_manager ? `${patient.case_manager.first_name || ''} ${patient.case_manager.last_name || ''}`.trim() || "" : ""}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.label}>Finalized By:</span>
                <span className={styles.value}>{finalizedByName || ""}</span>
              </div>
            </div>
            <div className={styles.gridColumn}>
              <div className={styles.infoItem}>
                <span className={styles.label}>Finalization Date:</span>
                <span className={styles.value}>{finalizedDateDisplay || ""}</span>
              </div>
            </div>
          </div>
        </div>

        {/* CPT Codes */}
        {billingData?.ccm_billings?.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.blueBar}></div>
              <h2 className={styles.sectionTitle}>CPT Codes Generated</h2>
            </div>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: "15%", textAlign: "start" }}>Code</th>
                  <th style={{ width: "10%", textAlign: "start" }}>Units</th>
                  <th style={{ width: "75%", textAlign: "start" }}>Description</th>
                </tr>
              </thead>
              <tbody>
                {billingData.ccm_billings.map((bill, index) => (
                  <tr key={index} className={styles.tableRow}>
                    <td>{bill.code}</td>
                    <td>{bill.units}</td>
                    <td>{bill.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Health Summary */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.blueBar}></div>
            <h2 className={styles.sectionTitle}>Health Summary</h2>
          </div>

          <div className={styles.subSectionTitle}>Physiological Averages</div>
          <div className={styles.gridContainer}>
            <div className={styles.gridColumn}>
              <div className={styles.infoItem}>
                <span className={styles.label}>Blood Pressure:</span>
                <span className={styles.value}>{carePlanData?.data?.physiological_data?.blood_pressure || ""} mm Hg</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.label}>Weight:</span>
                <span className={styles.value}>{carePlanData?.data?.physiological_data?.weight || ""} lbs</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.label}>Blood Glucose:</span>
                <span className={styles.value}>{carePlanData?.data?.physiological_data?.blood_glucose || ""} mg/dL</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.label}>BMI:</span>
                <span className={styles.value}>{carePlanData?.data?.physiological_data?.bmi ? `${carePlanData.data.physiological_data.bmi} kg/m2` : ""}</span>
              </div>
            </div>
            <div className={styles.gridColumn}>
              <div className={styles.infoItem}>
                <span className={styles.label}>Height:</span>
                <span className={styles.value}>{carePlanData?.data?.physiological_data?.height || ""} {getHeightUnit(carePlanData?.data?.created_at)}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.label}>DOB Confirmed:</span>
                <span className={styles.value}>{carePlanData?.data?.dob_confirmed ? "Yes" : "No"}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.label}>Consent for Monthly Calls:</span>
                <span className={styles.value}>{carePlanData?.data?.consent_for_calls ? "Yes" : "No"}</span>
              </div>
            </div>
          </div>

          <div className={styles.subSectionTitle}>Behavioral Averages</div>
          <div className={styles.gridContainer}>
            <div className={styles.gridColumn}>
              <div className={styles.infoItem}>
                <span className={styles.label}>Sleep:</span>
                <span className={styles.value}>{getBehavioralVal('sleep_hours', 'sleep')}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.label}>Exercise:</span>
                <span className={styles.value}>{getBehavioralVal('exercise_hours_per_week', 'exercise')}</span>
              </div>
            </div>
            <div className={styles.gridColumn}>
              <div className={styles.infoItem}>
                <span className={styles.label}>Diet/Water:</span>
                <span className={styles.value}>{getBehavioralVal('water_diet', 'diet_per_day')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Problem List */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.blueBar}></div>
            <h2 className={styles.sectionTitle}>Problem List & Education</h2>
          </div>

          <div className={styles.subSectionTitle}>PMHx</div>
          <div className={styles.listContainer}>
            {carePlanData?.data?.pmhx?.length > 0 ? (
              carePlanData.data.pmhx.map((item, i) => (
                <div key={i} className={styles.listItem}>• {renderBoldText(item)}</div>
              ))
            ) : (
              <div className={styles.emptyText}></div>
            )}
          </div>

          {/* SCRUM-941: Barriers and Recommendations are numbered; blank rows skipped. */}
          <div className={styles.subSectionTitle}>Potential Barriers</div>
          <div className={styles.listContainer}>
            {renderList((carePlanData?.data?.potential_barriers || []).join("\n"), true)}
          </div>

          <div className={styles.subSectionTitle}>Recent Hospitalization</div>
          <div className={styles.listContainer}>
            {carePlanData?.data?.recent_hospitalization?.length > 0 ? (
              carePlanData.data.recent_hospitalization.map((item, i) => (
                <div key={i} className={styles.listItem}>• {renderBoldText(item)}</div>
              ))
            ) : (
              <div className={styles.emptyText}></div>
            )}
          </div>

          <div className={styles.subSectionTitle}>Recommendations</div>
          <div className={styles.listContainer}>
            {renderList((carePlanData?.data?.recommendations || []).join("\n"), true)}
          </div>
        </div>

        {/* Medication Reconciliation (includes Medications) */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.blueBar}></div>
            <h2 className={styles.sectionTitle}>Medication Reconciliation</h2>
          </div>

          {/* Medications List */}
          <div className={styles.subSectionTitle}>Medications</div>
          <div className={styles.listContainer}>
            {rawPatientMedications?.length > 0 ? (
              rawPatientMedications.map((med, index) => (
                <div key={index} className={styles.listItem}>{med.name && `• ${med.name}`}</div>
              ))
            ) : (
              <div className={styles.emptyText}>No medications recorded.</div>
            )}
          </div>

          <div className={styles.gridContainer} style={{ marginTop: '15px' }}>
            <div className={styles.gridColumn}>
              <div className={styles.infoItem}>
                <span className={styles.label}>Allergies:</span>
                <div style={{ flex: 1 }}>
                  {carePlanData?.data?.allergies?.length > 0 ? (
                    carePlanData.data.allergies.map((item, i) => (
                      <div key={i} className={styles.value}>{renderBoldText(item)}</div>
                    ))
                  ) : (
                    <span className={styles.value}></span>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.gridColumn}>
              <div className={styles.infoItem}>
                <span className={styles.label}>Side Effects:</span>
                <div style={{ flex: 1 }}>
                  {carePlanData?.data?.reported_side_effect?.length > 0 ? (
                    carePlanData.data.reported_side_effect.map((item, i) => (
                      <div key={i} className={styles.value}>{renderBoldText(item)}</div>
                    ))
                  ) : (
                    <span className={styles.value}></span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Functional Assessment */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.blueBar}></div>
            <h2 className={styles.sectionTitle}>Functional Assessment</h2>
          </div>
          <div className={styles.gridContainer}>
            <div className={styles.gridColumn}>
              <div className={styles.infoItem}>
                <span className={styles.label}>ADLs:</span>
                <div style={{ flex: 1 }}>
                  {carePlanData?.data?.activities_of_daily_living?.length > 0 ? (
                    carePlanData.data.activities_of_daily_living.map((item, i) => (
                      <div key={i} className={styles.value}>• {renderBoldText(item)}</div>
                    ))
                  ) : (
                    <span className={styles.value}></span>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.gridColumn}>
              <div className={styles.infoItem}>
                <span className={styles.label}>Caregiver Assessment:</span>
                <div style={{ flex: 1 }}>
                  {carePlanData?.data?.caregiver_assessment?.length > 0 ? (
                    carePlanData.data.caregiver_assessment.map((item, i) => (
                      <div key={i} className={styles.value}>• {renderBoldText(item)}</div>
                    ))
                  ) : (
                    <span className={styles.value}></span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chronic Conditions */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.blueBar}></div>
            <h2 className={styles.sectionTitle}>Chronic Conditions</h2>
          </div>
          {rawPatientConditions?.length > 0 ? (
            rawPatientConditions.map((cond, index) => (
              <div key={index} className={styles.conditionBlock}>
                <div className={styles.conditionTitle}>
                  Condition #{index + 1}: {cond.name || ""}
                </div>
                <div className={styles.infoItem} style={{flexDirection: 'column', alignItems: 'flex-start'}}>
                  <span className={styles.label}>Prognosis:</span>
                  <div style={{marginLeft: 10}}>{renderList(cond.prognosis)}</div>
                </div>
                <div className={styles.infoItem} style={{flexDirection: 'column', alignItems: 'flex-start'}}>
                  <span className={styles.label}>Symptoms:</span>
                  <div style={{marginLeft: 10}}>
                    {wasCreatedOnOrAfterSymptomsCutoff(carePlanData?.data?.created_at)
                      ? renderSymptoms(cond.symptoms)
                      : renderList(cond.symptoms)}
                  </div>
                </div>
                <div className={styles.infoItem} style={{flexDirection: 'column', alignItems: 'flex-start'}}>
                  <span className={styles.label}>Treatment Goals:</span>
                  <div style={{marginLeft: 10}}>{renderList(cond.treatment_goals, true)}</div>
                </div>
                <div className={styles.infoItem} style={{flexDirection: 'column', alignItems: 'flex-start'}}>
                  <span className={styles.label}>Planned Interventions:</span>
                  <div style={{marginLeft: 10}}>
                    {cond.planned_interventions ? (
                      cond.planned_interventions.split("\n").filter((line) => line.trim()).map((line, i) => {
                        const parts = line.split(":::");
                        const text = parts.length > 1 ? `${parts[0]}: ${parts[1]}` : line;
                        return <div key={i} className={styles.listItem}>{i + 1}. {renderBoldText(text)}</div>;
                      })
                    ) : (
                      <div className={styles.listItem}></div>
                    )}
                  </div>
                </div>
                <div style={{ marginTop: 5, fontSize: "12px", fontWeight: "bold" }}>
                  ICD-10 Code: {cond.icd_code || ""}
                </div>
              </div>
            ))
          ) : (
            <div className={styles.emptyText}>No chronic conditions recorded.</div>
          )}
        </div>

        {/* Careplan Notes */}
        {carePlanData?.data?.note && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.blueBar}></div>
              <h2 className={styles.sectionTitle}>Careplan Notes</h2>
            </div>
            <div className={styles.listItem} style={{ whiteSpace: "pre-wrap" }}>
              {renderBoldText(carePlanData.data.note)}
            </div>
          </div>
        )}

        {/* CCM Notes - RENAMED AND FILTERED */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.blueBar}></div>
            <h2 className={styles.sectionTitle}>CCM Notes</h2>
          </div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: "20%", textAlign: "start" }}>Date</th>
                <th style={{ width: "60%", textAlign: "start" }}>Text</th>
                <th style={{ width: "20%", textAlign: "start" }}>Creator</th>
              </tr>
            </thead>
            <tbody>
              {ccmNotes.length > 0 ? (
                ccmNotes.map((note, index) => (
                  <tr key={index} className={styles.tableRow}>
                    <td>{formatUSADateTime(note.created_at)}</td>
                    <td>{note.note_text}</td>
                    <td>{note.assigned_by_name || note.assigned_by || ""}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className={styles.emptyText}>No CCM notes available.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Monitoring Sessions */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.blueBar}></div>
            <h2 className={styles.sectionTitle}>Monitoring Sessions</h2>
          </div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: "30%", textAlign: "start" }}>Date</th>
                <th style={{ width: "25%", textAlign: "start" }}>Duration</th>
                <th style={{ width: "45%", textAlign: "start" }}>Staff</th>
              </tr>
            </thead>
            <tbody>
              {ccmMonitoringSessions.length > 0 ? (
                ccmMonitoringSessions.map((session, index) => (
                  <tr key={index} className={styles.tableRow}>
                    <td>{formatNaiveDateTime(session.created_at)}</td>
                    <td>{formatSecondsToTime(session.total_ccm_seconds)}</td>
                    <td>{session.staff_member?.full_name || session.assigned_by_name || ""}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className={styles.emptyText}>No monitoring sessions recorded.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Attestation */}
        <div className={styles.attestationContainer}>
          <div className={styles.attestationText}>
            I attest that the Chronic Care Management services documented in this report were provided
            under my supervision and reflect accurate and honest clinical care delivered during the
            specified time period.
          </div>
          <div className={styles.attestationSigRow}>
            <span className={styles.attestationSig}>
              Electronically Signed By: {finalizedByName}
            </span>
            <span className={styles.attestationSig}>Date: {finalizedDateDisplay}</span>
          </div>
        </div>
        <div className={styles.previewFooter}>
          <span>{patientName} - CCM Audit Report</span>
          <span>1 of 1</span>
          <span>
            {new Date().toLocaleString("en-US", {
              month: "2-digit",
              day: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true
            })}
          </span>
        </div>
      </div>
    </>
  );
};

export default CareplanViewPdf;